import { setupAxios } from '../app/modules/auth'
import { api } from './APIMiddlewares'
import { API_BASE_URL } from '../utils/contants'

// Get all incidents with filtering
export async function getIncidents(filters: {
  status?: string
  categoryCode?: string
  priority?: string
  assignedToId?: string
  reporterId?: string
  isAnonymous?: boolean
  view?: 'mine' | 'assigned'
  page?: number
  limit?: number
}) {
  const { token, tenantId } = setupAxios()
  
  const queryParams = new URLSearchParams()
  if (filters.status) queryParams.append('status', filters.status)
  if (filters.categoryCode) queryParams.append('categoryCode', filters.categoryCode)
  if (filters.priority) queryParams.append('priority', filters.priority)
  if (filters.assignedToId) queryParams.append('assignedToId', filters.assignedToId)
  if (filters.reporterId) queryParams.append('reporterId', filters.reporterId)
  if (filters.isAnonymous !== undefined) queryParams.append('isAnonymous', String(filters.isAnonymous))
  if (filters.view) queryParams.append('view', filters.view)
  if (filters.page) queryParams.append('page', String(filters.page))
  if (filters.limit) queryParams.append('limit', String(filters.limit))
  
  return await api.get(
    `/incidents?${queryParams.toString()}`,
    token,
    tenantId,
    false
  )
}

// Get next case ID
export async function getNextCaseId() {
  const { token, tenantId } = setupAxios()
  return await api.get('/incidents/next-case-id', token, tenantId, false)
} 

// Get assignable users
export async function getAssignableUsers(includePosh = false) {
  const { token, tenantId } = setupAxios()
  return await api.get(`/incidents/assignable-users${includePosh ? '?includePosh=true' : ''}`, token, tenantId, false)
}

export async function getIncidentRateLimitStatus() {
  const { token, tenantId } = setupAxios()
  return await api.get('/incidents/rate-limit', token, tenantId, false)
}

// Get incident by ID
export async function getIncidentById(id: string) {
  const { token, tenantId } = setupAxios()
  return await api.get(`/incidents/${id}`, token, tenantId, false)
}

// Create new incident
export async function createIncident(data: {
  caseId: string
  categoryCode: string
  incidentDate: string
  description: string
  peopleInvolved?: string
  locationContext?: string
  isAnonymous?: boolean
  anonReporterId?: string
  status?: string
  priority?: string
  assignedToId?: string
  reporterId?: string
  slaDueDate?: string
}) {
  const { token, tenantId } = setupAxios()
  return await api.post(
    '/incidents',
    token,
    tenantId,
    {
      body: JSON.stringify(data),
    },
    false
  )
}

// Update incident
export async function updateIncident(id: string, data: {
  categoryCode?: string
  incidentDate?: string
  description?: string
  peopleInvolved?: string
  locationContext?: string
  priority?: string
  slaDueDate?: string
  resolutionSummary?: string
  reporterId?: string
}) {
  const { token, tenantId } = setupAxios()
  return await api.put(
    `/incidents/${id}`,
    token,
    tenantId,
    {
      body: JSON.stringify(data),
    },
    false
  )
}

// Delete incident
export async function deleteIncident(id: string) {
  const { token, tenantId } = setupAxios()
  return await api.delete(`/incidents/${id}`, token, tenantId, false, undefined, true)
}

// Assign incident to user
export async function assignIncident(id: string, data: {
  assignedToId: string
  remarks?: string
}) {
  const { token, tenantId } = setupAxios()
  return await api.post(
    `/incidents/${id}/assign`,
    token,
    tenantId,
    {
      body: JSON.stringify(data),
    },
    false
  )
}

// Update incident status
export async function updateIncidentStatus(id: string, data: {
  status: string
  remarks?: string
}) {
  const { token, tenantId } = setupAxios()
  return await api.put(
    `/incidents/${id}/status`,
    token,
    tenantId,
    {
      body: JSON.stringify(data),
    },
    false
  )
}

// Get comments for incident
export async function getIncidentComments(incidentId: string) {
  const { token, tenantId } = setupAxios()
  return await api.get(`/incidents/${incidentId}/comments`, token, tenantId, false)
}

export async function getIncidentAuditTrail(incidentId: string) {
  const { token, tenantId } = setupAxios()
  return await api.get(`/incidents/${incidentId}/audit`, token, tenantId, false)
}

// Create comment on incident
export async function createIncidentComment(incidentId: string, data: {
  commentType: string
  body: string
  visibleToUserIds?: string[]
}) {
  const { token, tenantId } = setupAxios()
  return await api.post(
    `/incidents/${incidentId}/comments`,
    token,
    tenantId,
    {
      body: JSON.stringify(data),
    },
    false
  )
}

// Get attachments for incident
export async function getIncidentAttachments(incidentId: string) {
  const { token, tenantId } = setupAxios()
  return await api.get(`/incidents/${incidentId}/attachments`, token, tenantId, false)
}

export async function getIncidentAttachmentFile(attachmentId: string, download = false) {
  const { token, tenantId } = setupAxios()
  const action = download ? 'download' : 'view'
  const response = await fetch(`${API_BASE_URL}/incidents/attachments/${attachmentId}/file?action=${action}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Tenant-Id': tenantId,
    },
  })
  if (!response.ok) {
    throw new Error(response.status === 403 ? 'You are not authorized to open this attachment.' : 'Unable to retrieve attachment.')
  }
  return response.blob()
}

// Upload attachment to incident
export async function uploadIncidentAttachment(incidentId: string, file: File) {
  const { token, tenantId } = setupAxios()
  const formData = new FormData()
  formData.append('file', file)
  
  return await api.post(
    `/incidents/${incidentId}/attachments`,
    token,
    tenantId,
    {
      body: formData,
    },
    false,
    true
  )
}

// Delete attachment
export async function deleteIncidentAttachment(attachmentId: string) {
  const { token, tenantId } = setupAxios()
  return await api.delete(`/incidents/attachments/${attachmentId}`, token, tenantId, false)
}

// Get incident statistics
export async function getIncidentStats() {
  const { token, tenantId } = setupAxios()
  return await api.get('/incidents/stats/dashboard', token, tenantId, false)
}

// ==================== POSH Committee APIs ====================

// Get all POSH committees
export async function getPoshCommittees(filters?: {
  page?: number
  limit?: number
}) {
  const { token, tenantId } = setupAxios()
  const queryParams = new URLSearchParams()
  if (filters?.page) queryParams.append('page', String(filters.page))
  if (filters?.limit) queryParams.append('limit', String(filters.limit))

  return await api.get(
    `/posh-committees?${queryParams.toString()}`,
    token,
    tenantId,
    false
  )
}

// Get POSH committee by ID
export async function getPoshCommitteeById(id: string) {
  const { token, tenantId } = setupAxios()
  return await api.get(`/posh-committees/${id}`, token, tenantId, false)
}

// Create POSH committee
export async function createPoshCommittee(data: {
  name: string
  description?: string
  memberIds: string[]
}) {
  const { token, tenantId } = setupAxios()
  return await api.post(
    '/posh-committees',
    token,
    tenantId,
    {
      body: JSON.stringify(data),
    },
    false
  )
}

// Update POSH committee
export async function updatePoshCommittee(id: string, data: {
  name?: string
  description?: string
  memberIds?: string[]
}) {
  const { token, tenantId } = setupAxios()
  return await api.put(
    `/posh-committees/${id}`,
    token,
    tenantId,
    {
      body: JSON.stringify(data),
    },
    false
  )
}

// Delete POSH committee
export async function deletePoshCommittee(id: string) {
  const { token, tenantId } = setupAxios()
  return await api.delete(`/posh-committees/${id}`, token, tenantId, false, undefined, true)
}

// Remove employee from POSH committee
export async function deletePoshCommitteeMember(
  committeeId: string,
  userId: string
) {
  const { token, tenantId } = setupAxios()
  return await api.delete(
    `/posh-committees/${committeeId}/members/${userId}`,
    token,
    tenantId,
    false,
    undefined,
    true
  )
}

// Get active POSH committee members (for dropdown)
export async function getPoshCommitteeMembers() {
  const { token, tenantId } = setupAxios()
  return await api.get('/posh-committees/members/active', token, tenantId, false)
}

// Add employee to existing POSH committee
export async function addPoshCommitteeMember(
  committeeId: string,
  userId: string
) {
  const { token, tenantId } = setupAxios()
  return await api.post(
    `/posh-committees/${committeeId}/members`,
    token,
    tenantId,
    {
      body: JSON.stringify({
        userId,
      }),
    },
    false
  )
}

// ==================== Incident Category APIs ====================

// Get all incident categories (Super Admin only)
export async function getIncidentCategories(filters?: { page?: number; limit?: number; isActive?: boolean }) {
  const { token, tenantId } = setupAxios()
  const queryParams = new URLSearchParams()
  if (filters?.page) queryParams.append('page', String(filters.page))
  if (filters?.limit) queryParams.append('limit', String(filters.limit))
  if (filters?.isActive !== undefined) queryParams.append('isActive', String(filters.isActive))
  return await api.get(`/incident-categories?${queryParams.toString()}`, token, tenantId, false)
}

// Get incident category by ID (Super Admin only)
export async function getIncidentCategoryById(id: string) {
  const { token, tenantId } = setupAxios()
  return await api.get(`/incident-categories/${id}`, token, tenantId, false)
}

// Create incident category (Super Admin only)
export async function createIncidentCategory(data: { code: string; name: string; description?: string; slaDays?: number }) {
  const { token, tenantId } = setupAxios()
  return await api.post(
    '/incident-categories',
    token,
    tenantId,
    {
      body: JSON.stringify(data),
    },
    false
  )
}

// Update incident category (Super Admin only)
export async function updateIncidentCategory(id: string, data: { name?: string; description?: string; isActive?: boolean; slaDays?: number }) {
  const { token, tenantId } = setupAxios()
  return await api.put(
    `/incident-categories/${id}`,
    token,
    tenantId,
    {
      body: JSON.stringify(data),
    },
    false
  )
}

// Delete incident category (Super Admin only)
export async function deleteIncidentCategory(id: string) {
  const { token, tenantId } = setupAxios()
  return await api.delete(`/incident-categories/${id}`, token, tenantId, false, undefined, true)
}

// Get active categories for dropdown (authenticated users)
export async function getActiveIncidentCategories() {
  const { token, tenantId } = setupAxios()
  return await api.get('/incident-categories/active', token, tenantId, false)
}
